// export class QueryBuilder {
import type * as Types from '@aeppic/document'

import { deprecated } from '@aeppic/shared/deprecation'

import * as QueryTypes from './types.js'
import { parseQueryAndFilter } from './match.js'
import { type DocumentIdentifier, readId } from '@aeppic/document'

export type ScopedQueryMatchOptions = QueryTypes.MatchOptions & {
  allowUnscopedQuery?: boolean
}

export interface SubQueryDefinitionFunction {
  (subQueryBuilder: QueryBuilder): void
}

export interface RelationshipQueryDefinitionFunction {
  (relationshipQueryBuilder: QueryBuilder): QueryBuilder|void
}

export type ComposableQuery = SubQueryDefinitionFunction | QueryBuilder | QueryTypes.Query | QueryTypes.Condition

type AndQueryBuilder = QueryBuilder & {
  (query?: ComposableQuery): QueryBuilder
}

type NotQueryBuilder = QueryBuilder & {
  (): NotQueryBuilder
  (query: ComposableQuery): QueryBuilder
}

type QueryParameterInput = string|number|boolean|(string|number|boolean)[]

type QueryScope = {
  field: string
  value: QueryParameterInput
}

type QueryBranch = {
  formIds: string[]
  formParameter?: QueryTypes.QueryParameterReference
  formParameterValue?: string|string[]
  conditions: QueryTypes.Condition[]
}

export function isQueryBuilder(builder: any): builder is QueryBuilder {
  return builder instanceof QueryBuilder
}

export function toQuery(query: QueryTypes.Query|QueryBuilder): QueryTypes.Query {
  if (isQueryBuilder(query)) {
    return query.toQuery()
  }
  return freezeQuerySnapshot(query)
}

export function cloneQuery(query: QueryTypes.Query): QueryTypes.Query {
  return cloneQuerySnapshot(query)
}

type DocumentMinimalReference = {
  id: Types.DocumentId
}

type ScopedDocumentReference = DocumentMinimalReference & {
  p?: Types.DocumentId
  a?: Types.DocumentId[]
  a_depth?: number
  a_forms?: Types.DocumentId[]
  f?: Types.Reference
}

type FormIdentity = {
  id: string
  v?: string
  text?: string
}

type FormReference = string | FormIdentity | { f: FormIdentity }

type SiblingScopeOptions = QueryTypes.MatchOptions & {
  includeSelf?: boolean
}

export class QueryScopes {
  private _defaultRoot?: Types.DocumentId|ScopedDocumentReference

  constructor(defaultRoot?: Types.DocumentId|ScopedDocumentReference) {
    this._defaultRoot = defaultRoot
  }

  filter(options: QueryTypes.MatchOptions = {}) {
    return new QueryBuilder(options)
  }

  global(options: QueryTypes.MatchOptions = {}) {
    return new ScopedQueryBuilder(null, { ...options, allowUnscopedQuery: true })
  }

  children(matchOptions?: QueryTypes.MatchOptions): ScopedQueryBuilder
  children(parents: DocumentMinimalReference[], matchOptions?: QueryTypes.MatchOptions): ScopedQueryBuilder
  children(parentIds: Types.DocumentId[], matchOptions?: QueryTypes.MatchOptions): ScopedQueryBuilder
  children(parent: DocumentMinimalReference, matchOptions?: QueryTypes.MatchOptions): ScopedQueryBuilder
  children(parentId: Types.DocumentId, matchOptions?: QueryTypes.MatchOptions): ScopedQueryBuilder
  children(arg1: QueryTypes.MatchOptions|Types.DocumentId|Types.DocumentId[]|DocumentMinimalReference|DocumentMinimalReference[], matchOptions?: QueryTypes.MatchOptions): ScopedQueryBuilder {
    let parentId: Types.DocumentId|Types.DocumentId[] = getDocumentIdFromRoot(this._defaultRoot)

    if (typeof arg1 === 'string') {
      parentId = arg1
    } else if (Array.isArray(arg1)) {
      parentId = arg1.map(parent => typeof parent === 'string' ? parent : parent.id)
    } else if (arg1 && 'id' in arg1) {
      parentId = arg1.id
    } else {
      matchOptions = arg1 as any
    }

    if (!parentId || (Array.isArray(parentId) && parentId.length === 0)) {
      throw new Error('Missing parentId')
    }

    const childrenCondition: QueryScope = {
      field: 'p',
      value: parentId
    }

    return new ScopedQueryBuilder(childrenCondition, matchOptions)
  }

  ancestorChildren(matchOptions?: QueryTypes.MatchOptions): ScopedQueryBuilder
  ancestorChildren(document: ScopedDocumentReference, matchOptions?: QueryTypes.MatchOptions): ScopedQueryBuilder
  ancestorChildren(arg1?: QueryTypes.MatchOptions|ScopedDocumentReference, matchOptions?: QueryTypes.MatchOptions): ScopedQueryBuilder {
    const { document, options } = this._resolveDocumentScopeArgument(arg1, matchOptions)

    if (!Array.isArray(document?.a)) {
      throw new Error('Missing document ancestry')
    }

    const ancestorChildrenCondition: QueryScope = {
      field: 'p',
      value: [...document.a]
    }

    return new ScopedQueryBuilder(ancestorChildrenCondition, options)
  }

  descendants(matchOptions?: QueryTypes.MatchOptions): ScopedQueryBuilder
  descendants(ancestor: ScopedDocumentReference, matchOptions?: QueryTypes.MatchOptions): ScopedQueryBuilder
  descendants(ancestorId: Types.DocumentId, matchOptions?: QueryTypes.MatchOptions): ScopedQueryBuilder
  descendants(arg1: QueryTypes.MatchOptions|Types.DocumentId|ScopedDocumentReference, matchOptions?: QueryTypes.MatchOptions): ScopedQueryBuilder {
    let ancestorId = getDocumentIdFromRoot(this._defaultRoot)
    let ancestorDocument = typeof this._defaultRoot === 'object' ? this._defaultRoot : undefined

    if (typeof arg1 === 'string') {
      ancestorId = arg1
      ancestorDocument = undefined
    } else if (arg1 && 'id' in arg1) {
      ancestorId = arg1.id
      ancestorDocument = arg1
    } else {
      matchOptions = arg1 as any
    }

    if (!ancestorId) {
      throw new Error('Missing parentId')
    }

    const ancestorDepth = getDocumentAncestryDepth(ancestorDocument)

    const descendantCondition: QueryScope = {
      field: ancestorDepth == null ? 'a' : `a.${ancestorDepth}`,
      value: ancestorId
    }

    if (ancestorDepth != null) {
      return new PreciseDescendantsQueryBuilder(descendantCondition, ancestorDepth, matchOptions)
    }

    return new ScopedQueryBuilder(descendantCondition, matchOptions)
  }

  ancestors(matchOptions?: QueryTypes.MatchOptions): ScopedQueryBuilder
  ancestors(document: ScopedDocumentReference, matchOptions?: QueryTypes.MatchOptions): ScopedQueryBuilder
  ancestors(documentId: Types.DocumentId, matchOptions?: QueryTypes.MatchOptions): ScopedQueryBuilder
  ancestors(arg1?: QueryTypes.MatchOptions|Types.DocumentId|ScopedDocumentReference, matchOptions?: QueryTypes.MatchOptions): ScopedQueryBuilder {
    const { document, options } = this._resolveDocumentScopeArgument(arg1, matchOptions)

    if (!Array.isArray(document?.a)) {
      throw new Error('Missing document ancestry')
    }

    const ancestorsCondition: QueryScope = {
      field: 'id',
      value: [...document.a]
    }

    return new AncestorsQueryBuilder(ancestorsCondition, document, options)
  }

  siblings(matchOptions?: SiblingScopeOptions): ScopedQueryBuilder
  siblings(document: ScopedDocumentReference, matchOptions?: SiblingScopeOptions): ScopedQueryBuilder
  siblings(documentId: Types.DocumentId, matchOptions?: SiblingScopeOptions): ScopedQueryBuilder
  siblings(arg1?: SiblingScopeOptions|Types.DocumentId|ScopedDocumentReference, matchOptions?: SiblingScopeOptions): ScopedQueryBuilder {
    const { document, options } = this._resolveDocumentScopeArgument<SiblingScopeOptions>(arg1, matchOptions)

    if (!document?.p) {
      throw new Error('Missing parentId')
    }

    const { includeSelf = false, ...queryOptions } = options ?? {}
    const siblingsCondition: QueryScope = {
      field: 'p',
      value: document.p
    }

    return new SiblingsQueryBuilder(siblingsCondition, document, { ...queryOptions, includeSelf })
  }

  similarSiblings(matchOptions?: SiblingScopeOptions): ScopedQueryBuilder
  similarSiblings(document: ScopedDocumentReference, matchOptions?: SiblingScopeOptions): ScopedQueryBuilder
  similarSiblings(documentId: Types.DocumentId, matchOptions?: SiblingScopeOptions): ScopedQueryBuilder
  similarSiblings(arg1?: SiblingScopeOptions|Types.DocumentId|ScopedDocumentReference, matchOptions?: SiblingScopeOptions): ScopedQueryBuilder {
    const { document, options } = this._resolveDocumentScopeArgument<SiblingScopeOptions>(arg1, matchOptions)

    if (!document?.f?.id) {
      throw new Error('Missing document form')
    }

    return this.siblings(document, options).sameForm(document) as ScopedQueryBuilder
  }

  private _resolveDocumentScopeArgument<TOptions extends QueryTypes.MatchOptions = QueryTypes.MatchOptions>(arg1?: TOptions|Types.DocumentId|ScopedDocumentReference, matchOptions?: TOptions): { document?: ScopedDocumentReference, options?: TOptions } {
    let document = this._defaultRoot
    let options = matchOptions

    if (typeof arg1 === 'string') {
      document = { id: arg1 }
    } else if (arg1 && 'id' in arg1) {
      document = arg1
    } else if (arg1) {
      options = arg1 as TOptions
    }

    return {
      document: typeof document === 'string' ? { id: document } : document,
      options
    }
  }
}

export const Scopes = new QueryScopes()
export type IScopes = typeof Scopes

type QueryBuilderOptions = { queryParameters?: Parameters, findOptions?: QueryTypes.QueryFindOptions } & QueryTypes.MatchOptions
export type RangeMatchOptions = {
  /** Whether both the lower and upper bounds are inclusive. Defaults to true. */
  inclusive?: boolean
  /**
   * The bounds are date-times compared as written on the wall clock, their
   * offset or `Z` cut off (no ordering by instant), e.g. a bound taken from
   * another document's date field. Aeppic 4 refuses an offset in a bound on
   * a date field in `data` without it.
   */
  comparedAsWritten?: boolean
}

class Parameters {
  private _parameterCount = 0
  private _parameters: QueryTypes.QueryParameters = []

  add(value: QueryParameterInput): QueryTypes.QueryParameterReference {
    const nextParameterId = this._parameterCount ++

    const parameter = { id: nextParameterId }
    this._parameters[nextParameterId] = value

    return parameter
  }

  replace(parameter: QueryTypes.QueryParameterReference, value: QueryParameterInput) {
    this._parameters[parameter.id] = value
  }

  toQueryParameters(): QueryTypes.QueryParameters {
    return this._parameters
  }
}

export class QueryBuilder {
  private _branches: QueryBranch[]
  private _currentBranch: QueryBranch

  private _matchOptions: QueryTypes.MatchOptions
  private _findOptions: QueryTypes.QueryFindOptions = {}
  private _finalized = false
  private _appendNextConditionAsOr = false
  private _parameters: Parameters
  private _andProxy: AndQueryBuilder
  private _notProxy: NotQueryBuilder

  constructor(options: QueryBuilderOptions = {}) {
    const { queryParameters, findOptions } = options
    const matchOptions: QueryTypes.MatchOptions = { ...options }
    delete (matchOptions as QueryBuilderOptions).queryParameters
    delete (matchOptions as QueryBuilderOptions).findOptions
    delete matchOptions.nowUtcTz

    this._currentBranch = createBranch()
    this._branches = [this._currentBranch]
    this._matchOptions = matchOptions
    this._findOptions = { ...(findOptions ?? {}) }

    this._parameters = queryParameters || new Parameters()
  }

  /** Create a builder by copying a query and rebinding its parameters. */
  static fromQuery(query: QueryTypes.Query): QueryBuilder {
    return new QueryBuilder(query.options).appendQuery(query)
  }

  // TODO: Include clone with matching spec.

  addParameter(value: QueryParameterInput): QueryTypes.QueryParameterReference {
    assertNotFinalized(this)
    return this._parameters.add(value)
  }

  protected replaceParameter(parameter: QueryTypes.QueryParameterReference, value: QueryParameterInput) {
    assertNotFinalized(this)
    this._parameters.replace(parameter, value)
  }

  get finalized() {
    return this._finalized
  }

  where(fieldPath: string): FieldCondition {
    assertNotFinalized(this)
    return new FieldCondition(fieldPath, this)
  }

  whereRelationship(relationship: string|QueryTypes.QueryRelationship, definition: RelationshipQueryDefinitionFunction): QueryBuilder {
    assertNotFinalized(this)
    const relationshipQueryBuilder = new QueryBuilder()
    const returnedBuilder = definition(relationshipQueryBuilder)
    const queryBuilder = isQueryBuilder(returnedBuilder) ? returnedBuilder : relationshipQueryBuilder

    return this.appendCondition({
      kind: 'relationship',
      conditions: [],
      relationship: normalizeRelationship(relationship),
      quantifier: 'some',
      query: queryBuilder._createQuery()
    })
  }

  form(formId: string): QueryBuilder {
    return this._appendFormIds([readFormIdentity(formId).id])
  }

  forms(formIds: string[]): QueryBuilder {
    return this._appendFormIds(formIds)
  }

  sameForm(formReference: FormReference): QueryBuilder {
    return this.form(readFormIdentity(formReference).id)
  }

  strictForm(formReference: FormReference): QueryBuilder {
    const formIdentity = readFormIdentity(formReference)

    if (!formIdentity.v) {
      throw new Error('Missing form version')
    }

    return this.form(formIdentity.id).where('f.v').is(formIdentity.v)
  }

  cloneOf(source: DocumentIdentifier): QueryBuilder {
    return this.where('cloneOf.id').is(readId(source))
  }

  cloneOfAny(sources: DocumentIdentifier[]): QueryBuilder {
    const sourceIds = sources.map(source => readId(source))
    return this.where('cloneOf.id').isOneOf(sourceIds)
  }

  whereQueryString(queryString: string): QueryBuilder {
    if (!queryString || queryString.trim() === '') {
      return this
    }

    return this._appendGraph(parseQueryAndFilter(queryString))
  }

  sort(sortOption: QueryTypes.QuerySortOption): QueryBuilder {
    return this._appendFindOptions({ sort: sortOption })
  }

  take(size: number): QueryBuilder {
    return this._appendFindOptions({ size })
  }

  stamp(stampTypeId: string) {
    return this.form('stamp').where('data.type.id').is(stampTypeId)
  }

  stamps(stampTypeIds: string[]|undefined) {
    let q = this.form('stamp')

    if (stampTypeIds) {
      return q.where('data.type.id').isOneOf(stampTypeIds)
    } else {
      return q
    }
  }

  stamped(stampTypeId: string|string[]) {
    const stampIdsToCheck = Array.isArray(stampTypeId) ? stampTypeId : [stampTypeId]
    return this.where('stamps.type.id').isOneOf(stampIdsToCheck)
  }

  get and(): AndQueryBuilder {
    assertNotFinalized(this)

    if (!this._andProxy) {
      this._andProxy = this._createCallableAndProxy()
    }

    return this._andProxy
  }

  get or(): QueryBuilder {
    assertNotFinalized(this)
    this._appendNextConditionAsOr = true
    return this
  }

  get not(): NotQueryBuilder {
    assertNotFinalized(this)

    if (!this._notProxy) {
      this._notProxy = this._createCallableNotProxy()
    }

    return this._notProxy
  }

  @deprecated('QueryBuilder', 'subQuery', 'and')
  subQuery(subQueryDefinitionFunction: SubQueryDefinitionFunction): QueryBuilder {
    return this._appendSubQuery(subQueryDefinitionFunction)
  }

  appendCondition(condition: QueryTypes.Condition) {
    assertNotFinalized(this)
    appendConditionToBranch(this._getBranchForNextCondition(), condition)
    return this
  }

  caseSensitive() {
    assertNotFinalized(this)
    const conditions = this._currentBranch.conditions
    const last = conditions[conditions.length - 1]

    if (last && QueryTypes.isFieldMatchNode(last)) {
      last.termQuotes = 'double'
    }

    return this
  }

  /**
   * Make the preceding `isStrict` / `isOneOfStrict` term ignore case. It stays
   * an exact value (no wildcards, no date math); plain terms already ignore case
   * and are left as they are.
   */
  caseInsensitive() {
    assertNotFinalized(this)
    const conditions = this._currentBranch.conditions
    const last = conditions[conditions.length - 1]

    if (last && QueryTypes.isFieldMatchNode(last) && last.termQuotes === 'double') {
      last.termQuotes = 'single'
    }

    return this
  }

  toQuery(): QueryTypes.Query {
    return freezeQuerySnapshot(this._createQuery())
  }

  toGraph(): QueryTypes.MatchExpression {
    return this.toQuery().graph
  }

  protected _createQuery(): QueryTypes.Query {
    const query: QueryTypes.Query = {
      graph: this._toGraph(),
      parameters: this._parameters.toQueryParameters(),
      options: this._matchOptions
    }

    if (QueryTypes.queryContainsRelationships(query)) {
      query.extensions = { relationships: 1 }
    }

    if (hasFindOptions(this._findOptions)) {
      query.findOptions = this._findOptions
    }

    return query
  }

  private _appendSubQuery(subQueryDefinitionFunction: SubQueryDefinitionFunction): QueryBuilder {
    return this._appendComposableQuery(subQueryDefinitionFunction)
  }

  private _appendComposableQuery(query: ComposableQuery): QueryBuilder {
    assertNotFinalized(this)

    if (typeof query === 'function') {
      return this._appendSubQueryDefinition(query)
    }

    return this._appendGraph(query)
  }

  private _appendNegatedComposableQuery(query: ComposableQuery): QueryBuilder {
    assertNotFinalized(this)

    if (typeof query === 'function') {
      const subQueryBuilder = new QueryBuilder({ queryParameters: this._parameters })
      query(subQueryBuilder)

      return this._appendNegatedComposableQuery(subQueryBuilder._createQuery())
    }

    const { graph, parameters, findOptions } = this._normalizeComposableQuery(query)
    const negatedGraph = negateMatchExpression(graph, this._parameters)

    return this._appendGraph({
      graph: negatedGraph,
      parameters,
      findOptions
    })
  }

  private _appendSubQueryDefinition(subQueryDefinitionFunction: SubQueryDefinitionFunction): QueryBuilder {
    const subQueryBuilder = new QueryBuilder({ queryParameters: this._parameters })
    subQueryDefinitionFunction(subQueryBuilder)

    const subQuery = subQueryBuilder._createQuery()

    return this._appendGraph(subQuery)
  }

  private _appendGraph(queryOrGraph: QueryBuilder | QueryTypes.Query | QueryTypes.Condition): QueryBuilder {
    const { graph, parameters, findOptions } = this._normalizeComposableQuery(queryOrGraph)

    if (!isEmptyExpression(graph) && this._wouldAppendFormConstraintAfterTerm(graph)) {
      throw new Error('form() and forms() can only be used before other filters in a scope or OR branch')
    }

    this._appendFindOptions(findOptions)

    if (isEmptyExpression(graph)) {
      return this
    }

    const targetParameters = this._parameters.toQueryParameters()
    const graphToAppend = parameters === targetParameters ? graph : copyConditionToParameters(graph, parameters, this._parameters) as QueryTypes.MatchExpression
    const branch = this._getBranchForNextCondition()

    if (isAndExpression(graphToAppend)) {
      for (const condition of graphToAppend.conditions) {
        appendConditionToBranch(branch, condition)
      }
    } else {
      appendConditionToBranch(branch, graphToAppend)
    }

    return this
  }

  /** Append a query's conditions while rebinding its parameters to this builder. */
  appendQuery(query: QueryTypes.Query): QueryBuilder {
    assertNotFinalized(this)
    return this._appendGraph(query)
  }

  private _appendFindOptions(findOptions?: QueryTypes.QueryFindOptions): QueryBuilder {
    assertNotFinalized(this)

    if (!findOptions) {
      return this
    }

    if (Object.prototype.hasOwnProperty.call(findOptions, 'sort')) {
      if (Object.prototype.hasOwnProperty.call(this._findOptions, 'sort')) {
        throw new Error('sort() can only be used once per query')
      }

      const sortOption = findOptions.sort
      this._findOptions.sort = sortOption === undefined ? sortOption : cloneSortOption(sortOption)
    }

    if (Object.prototype.hasOwnProperty.call(findOptions, 'size')) {
      if (Object.prototype.hasOwnProperty.call(this._findOptions, 'size')) {
        throw new Error('take() can only be used once per query')
      }

      this._findOptions.size = findOptions.size
    }

    return this
  }

  private _appendFormIds(formIds: string[]): QueryBuilder {
    assertNotFinalized(this)

    const branch = this._getBranchForNextCondition()

    if (branch.conditions.length > 0) {
      throw new Error('form() and forms() can only be used before other filters in a scope or OR branch')
    }

    for (const formId of formIds) {
      if (formId != null && !branch.formIds.includes(formId)) {
        branch.formIds.push(formId)
        branch.formParameter = undefined
        branch.formParameterValue = undefined
      }
    }

    return this
  }

  private _getBranchForNextCondition(): QueryBranch {
    if (this._appendNextConditionAsOr) {
      this._currentBranch = createBranch()
      this._branches.push(this._currentBranch)
      this._appendNextConditionAsOr = false
    }

    return this._currentBranch
  }

  private _wouldAppendFormConstraintAfterTerm(graph: QueryTypes.MatchExpression): boolean {
    if (!hasFormConstraint(graph)) {
      return false
    }

    const branch = this._appendNextConditionAsOr ? createBranch() : this._currentBranch
    return branch.conditions.length > 0
  }

  private _normalizeComposableQuery(queryOrGraph: QueryBuilder | QueryTypes.Query | QueryTypes.Condition): { graph: QueryTypes.MatchExpression, parameters: QueryTypes.QueryParameters, findOptions?: QueryTypes.QueryFindOptions } {
    if (isQueryBuilder(queryOrGraph)) {
      const query = queryOrGraph._createQuery()

      return {
        graph: query.graph,
        parameters: query.parameters,
        findOptions: query.findOptions
      }
    }

    if (QueryTypes.isQuery(queryOrGraph)) {
      return {
        graph: queryOrGraph.graph,
        parameters: queryOrGraph.parameters,
        findOptions: queryOrGraph.findOptions
      }
    }

    return {
      graph: conditionToExpression(queryOrGraph),
      parameters: this._parameters.toQueryParameters()
    }
  }

  private _createCallableAndProxy(): AndQueryBuilder {
    const builder = this
    const andFunction = ((query?: ComposableQuery) => {
      if (!query) {
        return builder
      }

      return builder._appendComposableQuery(query)
    }) as AndQueryBuilder

    return new Proxy(andFunction, {
      get(_target, property) {
        const value = (builder as unknown as Record<PropertyKey, unknown>)[property]
        return typeof value === 'function' && typeof value.bind === 'function' ? value.bind(builder) : value
      },

      set(_target, property, value) {
        (builder as unknown as Record<PropertyKey, unknown>)[property] = value
        return true
      }
    })
  }

  private _createCallableNotProxy(): NotQueryBuilder {
    const builder = this
    let notProxy: NotQueryBuilder

    const notFunction = ((query?: ComposableQuery) => {
      if (!query) {
        return notProxy
      }

      return builder._appendNegatedComposableQuery(query)
    }) as NotQueryBuilder

    notProxy = new Proxy(notFunction, {
      get(_target, property) {
        if (property === 'then') {
          return undefined
        }

        if (property === 'where') {
          return builder._whereNot.bind(builder)
        }

        if (property === 'not') {
          return builder.not
        }

        if (property === Symbol.toStringTag) {
          return 'NotQueryBuilder'
        }

        const value = (builder as unknown as Record<PropertyKey, unknown>)[property]

        if (typeof value === 'function') {
          return () => {
            throw createUnsupportedNotOperationError(String(property))
          }
        }

        return value
      },

      set(_target, property, value) {
        (builder as unknown as Record<PropertyKey, unknown>)[property] = value
        return true
      }
    }) as NotQueryBuilder

    return notProxy
  }

  private _whereNot(fieldPath: string): FieldCondition {
    assertNotFinalized(this)
    return new FieldCondition(fieldPath, this, { negated: true })
  }

  private _toGraph(): QueryTypes.MatchExpression {
    const branchConditions = this._branches
      .map(branch => this._buildBranchCondition(branch))
      .filter(isPresent)

    if (branchConditions.length === 0) {
      return { conditions: [] }
    }

    if (branchConditions.length === 1) {
      return conditionToExpression(branchConditions[0])
    }

    return {
      operator: 'OR',
      conditions: branchConditions
    }
  }

  private _buildBranchCondition(branch: QueryBranch): QueryTypes.Condition|null {
    const conditions = [
      ...this._buildFormConditions(branch),
      ...branch.conditions
    ]

    return conditionsToAndCondition(conditions)
  }

  private _buildFormConditions(branch: QueryBranch): QueryTypes.Condition[] {
    if (branch.formIds.length === 0) {
      return []
    }

    const formTerm = branch.formIds.length === 1 ? branch.formIds[0] : branch.formIds
    const formParameter = getFormParameter(branch, formTerm, this._parameters)

    return [{
      field: 'f.id',
      term: formParameter
    }]
  }
}

export class ScopedQueryBuilder extends QueryBuilder {
  protected _scopeField: string
  protected _scopeParameter: QueryTypes.QueryParameterReference

  constructor(scope: QueryScope, { allowUnscopedQuery = false, ...matchOptions }: ScopedQueryMatchOptions = {} ) {
    super(matchOptions)

    if (scope) {
      this._scopeField = scope.field
      this._scopeParameter = this.addParameter(scope.value)
    } else if (!allowUnscopedQuery) {
      throw new Error('Missing query scope. Either supply it or use option allowUnscopedQuery option')
    }
  }

  protected _createQuery(): QueryTypes.Query {
    const query = super._createQuery()

    if (this._scopeField) {
      ensureFieldMatchesParameter(query, this._scopeField, this._scopeParameter)
      return query
    } else {
      return query
    }
  }

  protected _replaceScopeValue(value: QueryParameterInput) {
    this.replaceParameter(this._scopeParameter, value)
  }
}

class PreciseDescendantsQueryBuilder extends ScopedQueryBuilder {
  private _minimumDepthParameter: QueryTypes.QueryParameterReference
  private _unboundedMaximumParameter: QueryTypes.QueryParameterReference

  constructor(scope: QueryScope, ancestorDepth: number, options: ScopedQueryMatchOptions = {}) {
    super(scope, options)
    this._minimumDepthParameter = this.addParameter(ancestorDepth.toString())
    this._unboundedMaximumParameter = this.addParameter('*')
  }

  protected _createQuery(): QueryTypes.Query {
    const query = super._createQuery()

    appendFieldRangeParameters(query, 'a_depth', this._minimumDepthParameter, this._unboundedMaximumParameter, { inclusive: false })
    return query
  }
}

class AncestorsQueryBuilder extends ScopedQueryBuilder {
  private _document: ScopedDocumentReference

  constructor(scope: QueryScope, document: ScopedDocumentReference, options: ScopedQueryMatchOptions = {}) {
    super(scope, options)
    this._document = document
  }

  form(formId: string): QueryBuilder {
    return this.forms([formId])
  }

  forms(formIds: string[]): QueryBuilder {
    if (!this._canResolveAncestorForms()) {
      return super.forms(formIds)
    }

    const ancestorIds = this._document.a.filter((_ancestorId, index) => {
      return formIds.includes(this._document.a_forms[index])
    })

    this._replaceScopeValue(toSingleOrMultiTerm(ancestorIds))
    return this
  }

  private _canResolveAncestorForms() {
    return Array.isArray(this._document.a) &&
      Array.isArray(this._document.a_forms) &&
      this._document.a.length === this._document.a_forms.length
  }
}

class SiblingsQueryBuilder extends ScopedQueryBuilder {
  private _excludedDocumentParameter?: QueryTypes.QueryParameterReference

  constructor(scope: QueryScope, document: ScopedDocumentReference, { includeSelf = false, ...options }: ScopedQueryMatchOptions & { includeSelf?: boolean } = {}) {
    super(scope, options)

    if (!includeSelf) {
      this._excludedDocumentParameter = this.addParameter(document.id)
    }
  }

  protected _createQuery(): QueryTypes.Query {
    const query = super._createQuery()

    if (this._excludedDocumentParameter) {
      appendFieldMatchesParameter(query, 'id', this._excludedDocumentParameter, { termPrefix: '-' })
    }

    return query
  }
}

function ensureFieldMatchesParameter(query: QueryTypes.Query, field: string, parameter: QueryTypes.QueryParameterReference) {
  const { graph } = query

  const newFieldCondition: QueryTypes.FieldMatchNode = {
    field,
    term: parameter
  }

  if (isAndExpression(graph)) {
    graph.conditions.unshift(newFieldCondition)

    if (graph.conditions.length > 1) {
      (graph as QueryTypes.MatchExpressionMultiMatch).operator = 'AND'
    }
  } else {
    query.graph = {
      operator: 'AND',
      conditions: [
        newFieldCondition,
        {
          operator: 'OR',
          conditions: graph.conditions
        }
      ]
    }
  }
}

function appendFieldMatchesParameter(query: QueryTypes.Query, field: string, parameter: QueryTypes.QueryParameterReference, { termPrefix }: { termPrefix?: string } = {}) {
  const { graph } = query

  const newFieldCondition: QueryTypes.FieldMatchNode = {
    field,
    term: parameter
  }

  if (termPrefix !== undefined) {
    newFieldCondition.termPrefix = termPrefix
  }

  if (isAndExpression(graph)) {
    graph.conditions.push(newFieldCondition)

    if (graph.conditions.length > 1) {
      (graph as QueryTypes.MatchExpressionMultiMatch).operator = 'AND'
    }
  } else {
    query.graph = {
      operator: 'AND',
      conditions: [
        {
          operator: 'OR',
          conditions: graph.conditions
        },
        newFieldCondition
      ]
    }
  }
}

function appendFieldRangeParameters(query: QueryTypes.Query, field: string, minimum: QueryTypes.QueryParameterReference, maximum: QueryTypes.QueryParameterReference, { inclusive = true }: { inclusive?: boolean } = {}) {
  const { graph } = query
  const newFieldCondition: QueryTypes.FieldMatchNode = {
    field,
    term_min: minimum,
    term_max: maximum,
    inclusive
  }

  if (isAndExpression(graph)) {
    graph.conditions.push(newFieldCondition)

    if (graph.conditions.length > 1) {
      (graph as QueryTypes.MatchExpressionMultiMatch).operator = 'AND'
    }
  } else {
    query.graph = {
      operator: 'AND',
      conditions: [
        {
          operator: 'OR',
          conditions: graph.conditions
        },
        newFieldCondition
      ]
    }
  }
}

class FieldCondition {
  constructor(private _fieldPath: string, private _builder: QueryBuilder, private _options: { negated?: boolean } = {}) {}

  is(value: number|string|boolean) {
    const parameter = this._builder.addParameter(value)
    // const parameter = this._builder.addParameter(value.toString())
    return this._appendParameterCondition(parameter, { operation: 'is' })
  }

  isNot(value: number|string|boolean) {
    const parameter = this._builder.addParameter(value)
    return this._appendParameterCondition(parameter, { operation: 'isNot', termPrefix: '-' })
  }

  isStrict(value: number|string|boolean) {
    if (typeof value !== 'string') {
      return this.is(value)
    }

    const parameter = this._builder.addParameter(value)
    return this._appendParameterCondition(parameter, { operation: 'isStrict', termQuotes: 'double' })
  }

  isOneOf(values: (number|string|boolean)[]) {
    const nonNullValues = values.filter(v => v != null)
    const parameter = this._builder.addParameter(nonNullValues)
    return this._appendParameterCondition(parameter, { operation: 'isOneOf' })
  }

  isAnyOf(values: (number|string|boolean)[]) {
    const nonNullValues = values.filter(v => v != null)
    const parameter = this._builder.addParameter(nonNullValues)
    return this._appendParameterCondition(parameter, { operation: 'isAnyOf' })
  }

  isOneOfStrict(values: (number|string|boolean)[]) {
    const nonNullValues = values.filter(v => v != null)
    const hasOnlyStrings = nonNullValues.every(v => typeof v === 'string')

    if (!hasOnlyStrings) {
      return this.isOneOf(nonNullValues)
    }

    const parameter = this._builder.addParameter(nonNullValues)
    return this._appendParameterCondition(parameter, { operation: 'isOneOfStrict', termQuotes: 'double' })
  }

  contains(value: string) {
    const parameter = this._builder.addParameter('*' + value + '*')
    return this._appendParameterCondition(parameter, { operation: 'contains', termQuotes: 'none' })
  }

  containsOneOf(values: string[]) {
    const parameter = this._builder.addParameter(values.map(v => '*' + v + '*'))
    return this._appendParameterCondition(parameter, { operation: 'containsOneOf', termQuotes: 'none' })
  }

  startsWith(value: string) {
    const parameter = this._builder.addParameter(value + '*')
    return this._appendParameterCondition(parameter, { operation: 'startsWith', termQuotes: 'none' })
  }

  startsWithOneOf(values: string[]) {
    const parameter = this._builder.addParameter(values.map(v => v + '*'))
    return this._appendParameterCondition(parameter, { operation: 'startsWithOneOf', termQuotes: 'none' })
  }

  endsWith(value: string) {
    const parameter = this._builder.addParameter('*' + value)
    return this._appendParameterCondition(parameter, { operation: 'endsWith', termQuotes: 'none' })
  }

  endsWithOneOf(values: string[]) {
    const parameter = this._builder.addParameter(values.map(v => '*' + v))
    return this._appendParameterCondition(parameter, { operation: 'endsWithOneOf', termQuotes: 'none' })
  }

  private _appendParameterCondition(parameter: QueryTypes.QueryParameterReference, { operation, termQuotes, termPrefix }: { operation: string, termQuotes?: string, termPrefix?: string }) {
    if (this._options.negated) {
      assertSupportedNotField(this._fieldPath, operation)
      termPrefix = toggleTermPrefix(termPrefix)
    }

    const condition: QueryTypes.FieldMatchNode = {
      field: this._fieldPath,
      term: parameter
    }

    if (termQuotes !== undefined) {
      condition.termQuotes = termQuotes
    }

    if (termPrefix !== undefined) {
      condition.termPrefix = termPrefix
    }

    return this._builder.appendCondition(condition)
  }

  isGreaterThan(value: number|string, options: Pick<RangeMatchOptions, 'comparedAsWritten'> = {}) {
    return this.isInRange(value, null, { ...options, inclusive: false })
  }

  isGreaterThanOrEqualTo(value: number|string, options: Pick<RangeMatchOptions, 'comparedAsWritten'> = {}) {
    return this.isInRange(value, null, options)
  }

  isLessThan(value: number|string, options: Pick<RangeMatchOptions, 'comparedAsWritten'> = {}) {
    return this.isInRange(null, value, { ...options, inclusive: false })
  }

  isLessThanOrEqualTo(value: number|string, options: Pick<RangeMatchOptions, 'comparedAsWritten'> = {}) {
    return this.isInRange(null, value, options)
  }

  isInRange(minValue: number|string|null, maxValue?: number|string|null, options: RangeMatchOptions = {}) {
    if (this._options.negated) {
      assertSupportedNotField(this._fieldPath, 'isInRange')
    }

    const termMin = minValue == null ? '*' : minValue.toString()
    const termMax = maxValue == null ? '*' : maxValue.toString()

    const minVariable = this._builder.addParameter(termMin)
    const maxVariable = this._builder.addParameter(termMax)

    const condition: QueryTypes.Condition = {
      field: this._fieldPath,
      term_min: minVariable,
      term_max: maxVariable,
    }

    if (options.inclusive !== undefined) {
      condition.inclusive = options.inclusive
    }

    if (options.comparedAsWritten) {
      condition.comparedAsWritten = true
    }

    return this._builder.appendCondition(condition)
  }

  isTruthy() {
    return this.isInRange('*', '*')
  }

  isTrue() {
    return this.isStrict(true)
  }

  isFalse() {
    return this.isStrict(false)
  }
}

function createBranch(): QueryBranch {
  return {
    formIds: [],
    conditions: []
  }
}

function getDocumentIdFromRoot(root: Types.DocumentId|DocumentMinimalReference|undefined): Types.DocumentId|undefined {
  if (typeof root === 'string') {
    return root
  }

  return root?.id
}

function getDocumentAncestryDepth(document: ScopedDocumentReference|undefined): number|undefined {
  if (!document) {
    return undefined
  }

  const ancestryDepth = Array.isArray(document.a) ? document.a.length : undefined
  const declaredDepth = document.a_depth

  if (declaredDepth != null && ancestryDepth != null && declaredDepth !== ancestryDepth) {
    throw new Error('Inconsistent document ancestry depth')
  }

  return declaredDepth ?? ancestryDepth
}

function readFormIdentity(formReference: FormReference): FormIdentity {
  if (typeof formReference === 'string') {
    return { id: formReference }
  }

  if ('f' in formReference) {
    return formReference.f
  }

  return formReference
}

function toSingleOrMultiTerm(values: string[]): string|string[] {
  return values.length === 1 ? values[0] : values
}

function getFormParameter(branch: QueryBranch, formTerm: string|string[], parameters: Parameters): QueryTypes.QueryParameterReference {
  if (!branch.formParameter || !sameFormTerm(branch.formParameterValue, formTerm)) {
    branch.formParameter = parameters.add(Array.isArray(formTerm) ? [...formTerm] : formTerm)
    branch.formParameterValue = Array.isArray(formTerm) ? [...formTerm] : formTerm
  }

  return branch.formParameter
}

function sameFormTerm(a: string|string[]|undefined, b: string|string[]): boolean {
  if (Array.isArray(a) || Array.isArray(b)) {
    return Array.isArray(a) && Array.isArray(b) && a.length === b.length && a.every((value, index) => value === b[index])
  }

  return a === b
}

function conditionToExpression(condition: QueryTypes.Condition): QueryTypes.MatchExpression {
  if (QueryTypes.isFieldMatchNode(condition) || QueryTypes.isRelationshipMatchNode(condition)) {
    return { conditions: [condition] }
  }

  return condition
}

function conditionsToAndCondition(conditions: QueryTypes.Condition[]): QueryTypes.Condition|null {
  if (conditions.length === 0) {
    return null
  }

  if (conditions.length === 1) {
    return conditions[0]
  }

  return {
    operator: 'AND',
    conditions
  }
}

function appendConditionToBranch(branch: QueryBranch, condition: QueryTypes.Condition) {
  if (!isOverrideableFieldCondition(condition)) {
    branch.conditions.push(condition)
    return
  }

  const existingConditionIndex = branch.conditions.findIndex(existingCondition => isSameFieldCondition(existingCondition, condition.field))

  if (existingConditionIndex < 0) {
    branch.conditions.push(condition)
    return
  }

  branch.conditions[existingConditionIndex] = condition
  branch.conditions = branch.conditions.filter((existingCondition, index) => {
    return index === existingConditionIndex || !isSameFieldCondition(existingCondition, condition.field)
  })
}

function isSameFieldCondition(condition: QueryTypes.Condition, field: string): boolean {
  return isOverrideableFieldCondition(condition) && condition.field === field
}

function isOverrideableFieldCondition(condition: QueryTypes.Condition): condition is QueryTypes.FieldMatchNode & { field: string } {
  return QueryTypes.isFieldMatchNode(condition) && typeof condition.field === 'string' && condition.field !== '' && !condition.termPrefix
}

function isEmptyExpression(expression: QueryTypes.MatchExpression) {
  return expression.conditions.length === 0
}

function negateMatchExpression(expression: QueryTypes.MatchExpression, parameters: Parameters): QueryTypes.MatchExpression {
  if (isEmptyExpression(expression)) {
    return createNeverMatchExpression(parameters)
  }

  return conditionToExpression(negateCondition(expression, parameters))
}

function negateCondition(condition: QueryTypes.Condition, parameters: Parameters): QueryTypes.Condition {
  if (QueryTypes.isFieldMatchNode(condition)) {
    return negateFieldCondition(condition)
  }

  if (QueryTypes.isRelationshipMatchNode(condition)) {
    throw createUnsupportedNotOperationError('whereRelationship(...)')
  }

  if (condition.conditions.length === 0) {
    return createNeverMatchExpression(parameters)
  }

  const operator = 'operator' in condition ? condition.operator : 'AND'

  if (operator !== 'AND' && operator !== 'OR') {
    throw createUnsupportedNotOperationError(operator)
  }

  const negatedConditions = condition.conditions.map(child => negateCondition(child, parameters))

  if (negatedConditions.length === 1) {
    return negatedConditions[0]
  }

  return {
    operator: operator === 'OR' ? 'AND' : 'OR',
    conditions: negatedConditions
  }
}

function negateFieldCondition(condition: QueryTypes.FieldMatchNode): QueryTypes.FieldMatchNode {
  assertSupportedNotFieldCondition(condition)

  const negatedCondition: QueryTypes.FieldMatchNode = {
    ...condition,
    termPrefix: toggleTermPrefix(condition.termPrefix)
  }

  if (negatedCondition.termPrefix === undefined) {
    delete negatedCondition.termPrefix
  }

  return negatedCondition
}

function assertSupportedNotFieldCondition(condition: QueryTypes.FieldMatchNode) {
  if (!isSupportedNotField(condition.field) ||
      !condition.term ||
      condition.term_min ||
      condition.term_max ||
      condition.termQuotes ||
      condition.fieldPrefix ||
      condition.similarity ||
      condition.boost) {
    throw createUnsupportedNotOperationError(`where('${condition.field}')`)
  }
}

function assertSupportedNotField(fieldPath: string, operation: string) {
  if (!isSupportedNotField(fieldPath) || !isSupportedNotOperation(operation)) {
    throw createUnsupportedNotOperationError(`where('${fieldPath}').${operation}`)
  }
}

function isSupportedNotField(fieldPath: string) {
  return fieldPath === 'id' || fieldPath === 'f.id'
}

function isSupportedNotOperation(operation: string) {
  return operation === 'is' || operation === 'isOneOf' || operation === 'isAnyOf'
}

function toggleTermPrefix(termPrefix: string|undefined) {
  return termPrefix === '-' ? undefined : '-'
}

function createNeverMatchExpression(parameters: Parameters): QueryTypes.MatchExpression {
  const impossible = parameters.add('__aeppic_query_never__')

  return {
    operator: 'AND',
    conditions: [
      {
        field: 'id',
        term: impossible
      },
      {
        field: 'id',
        term: impossible,
        termPrefix: '-'
      }
    ]
  }
}

function createUnsupportedNotOperationError(feature: string) {
  return new Error(`Feature not(${feature}) is not supported. QueryBuilder not() currently only supports where('id') and where('f.id') with is() or isAnyOf().`)
}

function isPresent<T>(value: T|null|undefined): value is T {
  return value != null
}

function hasFindOptions(findOptions: QueryTypes.QueryFindOptions): boolean {
  return Object.prototype.hasOwnProperty.call(findOptions, 'sort') ||
         Object.prototype.hasOwnProperty.call(findOptions, 'size')
}

function cloneFindOptions(findOptions: QueryTypes.QueryFindOptions): QueryTypes.QueryFindOptions {
  const clonedFindOptions: QueryTypes.QueryFindOptions = {}

  if (Object.prototype.hasOwnProperty.call(findOptions, 'sort')) {
    const sortOption = findOptions.sort
    clonedFindOptions.sort = sortOption === undefined ? sortOption : cloneSortOption(sortOption)
  }

  if (Object.prototype.hasOwnProperty.call(findOptions, 'size')) {
    clonedFindOptions.size = findOptions.size
  }

  return clonedFindOptions
}

function cloneSortOption(sortOption: QueryTypes.QuerySortOption): QueryTypes.QuerySortOption {
  if (Array.isArray(sortOption)) {
    return sortOption.map(option => cloneSortOptionEntry(option))
  }

  return cloneSortOptionEntry(sortOption)
}

function cloneSortOptionEntry(sortOption: string | QueryTypes.QueryFieldSortInfo): string | QueryTypes.QueryFieldSortInfo {
  if (typeof sortOption === 'string') {
    return sortOption
  }

  if ('fields' in sortOption) {
    return {
      ...sortOption,
      fields: [...sortOption.fields]
    }
  }

  if ('fallthrough' in sortOption) {
    return {
      ...sortOption,
      fallthrough: [...sortOption.fallthrough]
    }
  }

  return { ...sortOption }
}

function hasFormConstraint(condition: QueryTypes.Condition): boolean {
  if (QueryTypes.isFieldMatchNode(condition)) {
    return condition.field === 'f.id' && !condition.termPrefix
  }

  if (QueryTypes.isRelationshipMatchNode(condition)) {
    return false
  }

  return condition.conditions.some(child => hasFormConstraint(child))
}

function copyConditionToParameters(condition: QueryTypes.Condition, sourceParameters: QueryTypes.QueryParameters, targetParameters: Parameters): QueryTypes.Condition {
  if (QueryTypes.isFieldMatchNode(condition)) {
    return copyFieldConditionToParameters(condition, sourceParameters, targetParameters)
  }

  if (QueryTypes.isRelationshipMatchNode(condition)) {
    return cloneCondition(condition)
  }

  const expression: QueryTypes.MatchExpression = {
    conditions: condition.conditions.map(child => copyConditionToParameters(child, sourceParameters, targetParameters))
  }

  if ('operator' in condition) {
    return {
      ...expression,
      operator: condition.operator
    }
  }

  return expression
}

function copyFieldConditionToParameters(condition: QueryTypes.FieldMatchNode, sourceParameters: QueryTypes.QueryParameters, targetParameters: Parameters): QueryTypes.FieldMatchNode {
  const copiedCondition = { ...condition }

  if (copiedCondition.term) {
    copiedCondition.term = copyParameterReference(copiedCondition.term, sourceParameters, targetParameters)
  }

  if (copiedCondition.term_min) {
    copiedCondition.term_min = copyParameterReference(copiedCondition.term_min, sourceParameters, targetParameters)
  }

  if (copiedCondition.term_max) {
    copiedCondition.term_max = copyParameterReference(copiedCondition.term_max, sourceParameters, targetParameters)
  }

  return copiedCondition
}

function copyParameterReference(parameterReference: QueryTypes.QueryParameterReference, sourceParameters: QueryTypes.QueryParameters, targetParameters: Parameters): QueryTypes.QueryParameterReference {
  if (!sourceParameters || !Object.prototype.hasOwnProperty.call(sourceParameters, parameterReference.id)) {
    return parameterReference
  }

  return targetParameters.add(sourceParameters[parameterReference.id] as any)
}

function assertNotFinalized(object: { finalized: boolean } ) {
  if (object.finalized) {
    throw new Error('Cannot perform this operation when already finalized')
  }
}

function isAndExpression(expression: QueryTypes.MatchExpression) {
  if (!('operator' in expression)) {
    return true
  }

  if (expression.operator === 'OR') {
    return false
  } else {
    return true
  }
}

function freezeQuerySnapshot(query: QueryTypes.Query): QueryTypes.Query {
  return deepFreeze(cloneQuerySnapshot(query))
}

function cloneQuerySnapshot(query: QueryTypes.Query): QueryTypes.Query {
  const snapshot: QueryTypes.Query = {
    graph: cloneCondition(query.graph) as QueryTypes.MatchExpression,
    parameters: cloneQueryParameters(query.parameters)
  }

  if (query.extensions) {
    snapshot.extensions = { ...query.extensions }
  }

  if (query.options) {
    snapshot.options = { ...query.options }
  }

  if (query.findOptions) {
    snapshot.findOptions = cloneFindOptions(query.findOptions)
  }

  return snapshot
}

function cloneCondition(condition: QueryTypes.Condition): QueryTypes.Condition {
  if (QueryTypes.isFieldMatchNode(condition)) {
    const fieldCondition: QueryTypes.FieldMatchNode = { ...condition }

    if (condition.term) {
      fieldCondition.term = { ...condition.term }
    }

    if (condition.term_min) {
      fieldCondition.term_min = { ...condition.term_min }
    }

    if (condition.term_max) {
      fieldCondition.term_max = { ...condition.term_max }
    }

    return fieldCondition
  }

  if (QueryTypes.isRelationshipMatchNode(condition)) {
    return {
      ...condition,
      relationship: { ...condition.relationship },
      query: cloneQuerySnapshot(condition.query)
    }
  }

  const expression: QueryTypes.MatchExpression = {
    conditions: condition.conditions.map(child => cloneCondition(child))
  }

  if ('operator' in condition) {
    return {
      ...expression,
      operator: condition.operator
    }
  }

  return expression
}

function cloneQueryParameters(parameters: QueryTypes.QueryParameters): QueryTypes.QueryParameters {
  if (Array.isArray(parameters)) {
    return parameters.map(value => cloneQueryParameterValue(value))
  }

  const copiedParameters: QueryTypes.StringQueryParameters = {}

  for (const [key, value] of Object.entries(parameters)) {
    copiedParameters[key] = cloneQueryParameterValue(value)
  }

  return copiedParameters
}

function cloneQueryParameterValue(value: QueryTypes.QueryParameterValue): QueryTypes.QueryParameterValue {
  return Array.isArray(value) ? [...value] : value
}

function deepFreeze<T>(value: T): T {
  if (!value || typeof value !== 'object' || Object.isFrozen(value)) {
    return value
  }

  for (const child of Object.values(value as Record<string, unknown>)) {
    deepFreeze(child)
  }

  Object.freeze(value)
  return value
}

function normalizeRelationship(relationship: string|QueryTypes.QueryRelationship): QueryTypes.QueryRelationship {
  if (typeof relationship === 'string') {
    const value = relationship.trim()
    if (!value) {
      throw new Error('Relationship name must not be empty')
    }

    return value[0] === value[0].toUpperCase()
      ? { type: 'named', name: value }
      : { type: 'reference', field: value }
  }

  if (relationship.type === 'reference') {
    if (!relationship.field?.trim()) {
      throw new Error('Reference relationship field must not be empty')
    }

    return { type: 'reference', field: relationship.field }
  }

  if (!relationship.name?.trim()) {
    throw new Error('Relationship name must not be empty')
  }

  return { type: 'named', name: relationship.name }
}

// function isOrExpression(expression: QueryTypes.MatchExpression) {
//   return !isAndExpression(expression)
// }

// function assert(condition: any, message?: string) {
//   if (!condition) {
//     throw new Error(message||'Assertion failed')
//   }
// }
